import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Rocket, TrendingUp, Calendar, ShoppingBag, DollarSign, Sparkles, Store, BarChart3, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { guidanceAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

const ICON_MAP = {
  rocket: Rocket,
  trending: TrendingUp,
  calendar: Calendar,
  shopping: ShoppingBag,
  dollar: DollarSign,
  sparkles: Sparkles,
  store: Store,
  chart: BarChart3,
};

const COLOR_MAP = {
  create: 'border-violet-500/20 bg-violet-500/5 hover:border-violet-500/40',
  invest: 'border-amber-500/20 bg-amber-500/5 hover:border-amber-500/40',
  buy: 'border-emerald-500/20 bg-emerald-500/5 hover:border-emerald-500/40',
  sell: 'border-blue-500/20 bg-blue-500/5 hover:border-blue-500/40',
  attend: 'border-rose-500/20 bg-rose-500/5 hover:border-rose-500/40',
};

const ICON_COLOR_MAP = {
  create: 'text-violet-400',
  invest: 'text-amber-400',
  buy: 'text-emerald-400',
  sell: 'text-blue-400',
  attend: 'text-rose-400',
};

export default function NextActionCards({ contextPage = 'home' }) {
  const { user } = useAuth();
  const [actions, setActions] = useState([]);

  useEffect(() => {
    if (!user) return;
    guidanceAPI.getNextActions(contextPage)
      .then(res => setActions(res.data?.actions || []))
      .catch(() => {});
  }, [user, contextPage]);

  if (!user || actions.length === 0) return null;

  return (
    <div data-testid="next-action-cards">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Suggested next</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {actions.map((action, idx) => {
          const Icon = ICON_MAP[action.icon] || Rocket;
          const colorClass = COLOR_MAP[action.type] || COLOR_MAP.buy;
          const iconColor = ICON_COLOR_MAP[action.type] || 'text-zinc-400';
          return (
            <motion.div
              key={action.link + idx}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.08 }}
            >
              <Link
                to={action.link}
                className={`block p-4 rounded-xl border transition-all group ${colorClass}`}
                data-testid={`next-action-${action.type}-${idx}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-zinc-800/80 flex items-center justify-center flex-shrink-0">
                    <Icon size={16} className={iconColor} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-zinc-200">{action.title}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">{action.desc}</p>
                  </div>
                  <ArrowRight size={14} className="text-zinc-600 group-hover:text-zinc-300 group-hover:translate-x-1 transition-all mt-1 flex-shrink-0 ml-auto" />
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
